import type { Signal } from '@preact/signals';
import { createContext } from 'preact';
import { useContext } from 'preact/hooks';
import type { AudioEngine } from '../audio/AudioEngine';
import type { GameController } from '../core/GameController';
import type { PlayController } from '../core/PlayController';
import type { Settings } from '../core/settings';
import type { GameState } from '../game/state';
import type { NetManager } from '../net/NetManager';
import { ui } from './store';

/** Everything the UI is allowed to talk to. */
export interface Services {
  game: GameController;
  play: PlayController;
  net: NetManager;
  audio: AudioEngine;
  settings: Signal<Settings>;
  updateSettings(patch: Partial<Settings>): void;
  startGame(): void;
  resetFarm(): void;
  hasSave: boolean;
}

export const ServicesContext = createContext<Services | null>(null);

export function useServices(): Services {
  const services = useContext(ServicesContext);
  if (!services) throw new Error('useServices must be used inside <ServicesContext.Provider>');
  return services;
}

/** The current game state; re-renders the component whenever it changes. */
export function useGameState(): GameState {
  const { game } = useServices();
  // Reading the signal subscribes this component to state changes.
  void ui.version.value;
  return game.state;
}
